<template>
  <div class="flex flex-row flew-wrap w-10">
    <HeadTitle
      title="Detalles periodos de calendarios de pagos de nómina."
      description="Formulario en donde los periodos de pago de nóminas."
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" class="mt-2" />
  </div>
  <br />
  <Step
    :step-number="1"
    title="Detalles del calendario de periodos"
    :is-last="false"
    :is-active="true"
  >
    <div class="formgrid grid w-12 md:w-6 mt-3">
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="frequency" class="text-gray-700 mb-1"
          >Frecuencia <span class="text-red-600">*</span></label
        >
        <Dropdown
          v-model="frequency"
          option-label="name"
          option-value="code"
          :options="frequencies"
          class="w-full"
          :disabled="!isEditMode"
        >
        </Dropdown>
        <small id="text-error" class="p-error">{{
          frequencyError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="reachOfConfig" class="text-gray-700 mb-1"
          >Alcance de la configuración
          <span class="text-red-600">*</span></label
        >
        <Dropdown
          v-model="selectedEntity"
          option-label="name"
          :options="entities"
          class="w-full"
          :disabled="!isEditMode"
        >
        </Dropdown>
      </div>
      <div
        v-if="selectedEntity.code === 'N'"
        class="field col-10 md:col-6 flex flex-column"
      >
        <label for="payrolls" class="text-gray-700 mb-1"
          >Seleccionar nóminas <span class="text-red-600">*</span></label
        >
        <MultiSelectDialog
          header-title="Seleccionar nóminas"
          label="Seleccionar nóminas"
          :options="payrolls || []"
          v-model="selectedPayrolls"
          show-key="name"
          value-key="id"
          :required="true"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          payrollsError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-12 md:col-6 flex flex-column col-span">
        <label for="description" class="text-gray-700 mb-1"
          >Descripción <span class="text-red-600">*</span></label
        >
        <InputText
          id="description"
          v-model="description"
          class="text-gray-700 mb-1"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
    </div>
  </Step>
  <Step :step-number="2" title="Crear periodos de nómina">
    <div class="formgrid grid w-12 md:w-7 mt-3">
      <div v-if="isEditMode" class="field col-12 md:col-4 flex flex-column">
        <label for="initDate" class="text-gray-700 mb-1"
          >Seleccionar tipo de generación de periodos</label
        >
        <Dropdown
          v-model="selectedGenerationTypePeriods"
          option-label="name"
          option-value="code"
          :options="generationTypesPeriodOptions"
          class="w-full"
          :disabled="!isEditMode"
        >
        </Dropdown>
      </div>
      <div
        v-if="selectedGenerationTypePeriods === 'AUTOMATIC'"
        class="grid gap-1 align-items-center w-full mt-2 ml-1"
      >
        <div class="field col-12 md:col-4 flex flex-column">
          <label for="initDate" class="text-gray-700 mb-1"
            >Fecha de inicio</label
          >
          <Calendar
            inputId="initDate"
            v-model="initDate"
            class="w-full"
            :disabled="!isEditMode"
            placeholder="DD/MM/YYYY"
            dateFormat="dd/mm/yy"
          />
          <small class="p-error">{{ initDateError || '&nbsp;' }}</small>
        </div>
        <div class="field col-12 md:col-4 flex flex-column">
          <label for="year" class="text-gray-700 mb-1"> Año objetivo </label>
          <InputText
            id="year"
            v-model="year"
            type="number"
            :min="1900"
            :max="2100"
            class="w-full"
            :disabled="!isEditMode"
          />
          <small class="p-error">{{ yearError || '&nbsp;' }}</small>
        </div>
        <div class="field col-12 md:col-2 flex flex-column">
          <Button
            icon="pi pi-bolt"
            severity="secondary"
            class="w-full"
            v-tooltip.bottom="'Generar periodos de pago'"
            @click="onGeneratePeriods"
            :disabled="!isEditMode"
          />
        </div>
        <div class="field col-12 md:col-1 flex flex-column">
          <InfoButton
            :message="`Genera los periodos de pagos desde una fecha, hasta el 13vo periodo del año objetivo.`"
          />
        </div>
      </div>
      <div
        v-else-if="selectedGenerationTypePeriods === 'FILE'"
        class="col-12 flex flex-row justify-content-start align-items-center mt-2"
      >
        <FileUpload
          mode="basic"
          accept=".csv, .xls, .xlsx"
          class="text-sm font-light"
          chooseLabel="Subir archivo"
          :disabled="!isEditMode"
          severity="secondary"
          custom-upload
          @uploader="removeFile"
          @select="saveFile($event)"
          style="background: #e5e7eb; border: #e4e4e4; color: #495057"
        >
          <i class="pi pi-upload"></i>
        </FileUpload>
        <InfoButton
          :message="`Sube un archivo en formato CSV o XLSX con las columnas FECHA_INICIO Y FECHA_FINAL`"
        />
      </div>
      <DataTable
        v-model:editingRows="editingRows"
        :value="periods"
        editMode="row"
        dataKey="id"
        tableClass="editable-cells-table"
        tableStyle="min-width: 30rem;"
        scrollHeight="600px"
        scrollable
        class="tabla w-10"
      >
        <template #header>
          <div
            class="table-header flex justify-content-between align-items-center"
          >
            <h5 class="m-0">
              Periodos de nómina<span class="text-red-600">*</span>
            </h5>
          </div>
        </template>
        <template #empty> No hay periodos de nominas. </template>
        <Column field="period" header="Periodo" :sortable="true"></Column>
        <Column
          field="startDate"
          header="Fecha de inicio"
          editor="calendar"
          :editorParams="{ mode: 'date' }"
          :sortable="true"
          :filter="true"
          filterPlaceholder="Buscar fecha de inicio"
        ></Column>
        <Column
          field="endDate"
          header="Fecha de fin"
          editor="calendar"
          :editorParams="{ mode: 'date' }"
          :sortable="true"
          :filter="true"
          filterPlaceholder="Buscar fecha de fin"
        ></Column>
      </DataTable>
      <small class="p-error">{{ periodsError || '&nbsp;' }}</small>
    </div>
  </Step>
  <Footer>
    <template #left>
      <AuditButton
        :record-ids="[calendarPeriodGroupId]"
        :entities="['CalendarPeriodsGroup']"
      />
    </template>
    <Button
      :label="isEditMode ? 'Cancelar' : 'Volver'"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="
        router.push('/payrollsConfig/' + payrollConfigId + '/calendarPeriods')
      "
    />
    <Button
      v-if="isEditMode"
      label="Guardar"
      class="text-sm font-light"
      severity="primary"
      @click="onSave"
    />
  </Footer>
</template>
<script lang="ts" setup>
  import * as yup from 'yup'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import Footer from '@/shared/components/Footer.vue'
  import Step from '@/shared/components/Step.vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import { onMounted, reactive, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useField } from 'vee-validate'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { getPayrollsByConfigId } from '@/modules/payrolls-config/services/PayrollService'
  import {
    generatePeriods,
    getCalendarPeriodsGroup,
    updateCalendarPeriodsGroup,
  } from '../services/CalendarPeriodsService'
  import dayjs from 'dayjs'
  import customParseFormat from 'dayjs/plugin/customParseFormat'
  dayjs.extend(customParseFormat)

  import Calendar from 'primevue/calendar'
  import { CalendarPeriod } from '../models/CalendarPeriod'
  import Column from 'primevue/column'
  import useLoading from '@/store/load'
  const payrollConfigId = ref()
  const path = useRoute()
  const router = useRouter()
  const loadingStore = useLoading()

  const calendarPeriodGroupId = ref()
  const payrolls = ref()
  const editingRows = ref()

  const selectedGenerationTypePeriods = ref()
  const generationTypesPeriodOptions = ref([
    { code: 'FILE', name: 'Carga masiva' },
    { code: 'AUTOMATIC', name: 'Automatica' },
  ])

  const yearSchema = yup
    .number()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value
    })
    .integer('El año debe ser un número entero')
    .required('El año es requerido')
    .min(1900, 'El año debe ser mayor a 1900')
    .max(2100, 'El año debe ser menor a 2100')

  const initDateSchema = yup.date().required('La fecha de inicio es requerida')
  const frequencySchema = yup.string().required('La frecuencia es requerida')
  const descriptionSchema = yup.string().required('La descripción es requerida')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')
  const periodsSchema = yup
    .array(
      yup.object({
        period: yup.string().required('El periodo es requerido'),
        startDate: yup.string().required('La fecha de inicio es requerida'),
        endDate: yup.string().required('La fecha de fin es requerida'),
      })
    )
    .required('Los periodos son requeridos')
    .min(1, 'Al menos un periodo debe ser creado')

  const {
    value: year,
    errorMessage: yearError,
    validate: yearValidate,
  } = useField<string>('year', yearSchema)
  const {
    value: frequency,
    errorMessage: frequencyError,
    validate: frequencyValidate,
  } = useField<string>('frequency', frequencySchema)
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descriptionValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<string[]>('selectedPayrolls', payrollsSchema)
  const {
    value: initDate,
    errorMessage: initDateError,
    validate: initDateValidate,
  } = useField<Date>('initDate', initDateSchema)
  const {
    value: periods,
    errorMessage: periodsError,
    validate: periodsValidate,
  } = useField<CalendarPeriod[]>('periods', periodsSchema)

  const entities = ref([
    { code: 'ALL', name: 'Toda las nóminas del grupo' },
    { code: 'N', name: 'Seleccionar Nóminas' },
  ])

  const frequencies = ref([
    { code: 'weekly', name: 'Semanal' },
    { code: 'biweekly', name: 'Bisemanal' },
  ])

  const selectedEntity = ref(entities.value[0])

  onMounted(async () => {
    const { calendarPeriodId } = path.params
    calendarPeriodGroupId.value = calendarPeriodId
    loadingStore.setLoadingState()
    const calendarPeriodsGroup = await getCalendarPeriodsGroup(
      String(calendarPeriodId)
    )
    payrollConfigId.value = calendarPeriodsGroup.payrollConfigId
    year.value = calendarPeriodsGroup.year.toString()
    frequency.value = calendarPeriodsGroup.frequency
    description.value = calendarPeriodsGroup.description
    initDate.value = dayjs(
      calendarPeriodsGroup.calendarPeriods[0]?.startDate,
      'DD/MM/YYYY'
    ).isValid()
      ? dayjs(
          calendarPeriodsGroup.calendarPeriods[0].startDate,
          'DD/MM/YYYY'
        ).toDate()
      : new Date()
    selectedEntity.value =
      calendarPeriodsGroup.onlyPayrolls.length > 0
        ? entities.value[1]
        : entities.value[0]
    selectedPayrolls.value = calendarPeriodsGroup.onlyPayrolls
    periods.value = calendarPeriodsGroup.calendarPeriods
    await fetchPayrolls()
    loadingStore.dismissLoadingState()
  })

  const onGeneratePeriods = async () => {
    try {
      await yearValidate()
      await frequencyValidate()
      await payrollsValidate()
      await initDateValidate()
      loadingStore.setLoadingState()
      const res = await generatePeriods({
        frequency: frequency.value,
        initialDate: dayjs(initDate.value).format('YYYY-MM-DD'),
        targetYear: dayjs(year.value).format('YYYY'),
      })
      loadingStore.dismissLoadingState()

      periods.value = res
    } catch (error) {
      console.error(error)
    }
  }

  const onSave = async () => {
    try {
      await frequencyValidate()
      await payrollsValidate()
      await descriptionValidate()

      loadingStore.setLoadingState()
      const res = await updateCalendarPeriodsGroup(
        calendarPeriodGroupId.value,
        {
          year: dayjs(periods.value[periods.value.length - 1].startDate).year(),
          frequency: frequency.value,
          description: description.value,
          calendarPeriods: periods.value,
          onlyPayrolls:
            selectedEntity.value.code === 'ALL' ? [] : selectedPayrolls.value,
          payrollConfigId: payrollConfigId.value,
        }
      )
      loadingStore.dismissLoadingState()
      if (res === 'OK') {
        router.push(
          '/payrollsConfig/' + payrollConfigId.value + '/calendarPeriods'
        )
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchPayrolls = async () => {
    if (payrollConfigId.value) {
      const response = await getPayrollsByConfigId(payrollConfigId.value)
      payrolls.value = response
    }
  }

  const isEditMode = ref<boolean>(false)
  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }

  const saveFile = async (event: any) => {
    const file = event.files[0]
    try {
      loadingStore.setLoadingState()
      const res = await generatePeriods({ file })
      loadingStore.dismissLoadingState()
      periods.value = res
    } catch (error) {
      console.error(error)
    }
  }

  const removeFile = (event: any) => {
    event.files = []
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  .actions {
    display: flex;
    max-width: 180px;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }
</style>
